import React, { useCallback, useEffect, useState } from "react";
import "./CreativeTray.scss";
import { Button, ButtonFrameworkVariant, ButtonType, DownloadDropdown } from "../../Components";
import { MdClose } from "react-icons/md";
import html2canvas from "html2canvas";
import downloadjs from "downloadjs";
import { awaitJSON, GetS3SignedUrl2, MetaLambdaFetch } from "../../utils/fetch-utils";
import {
  DisplayedMetrics,
  DISPLAYED_METRICS_LABELS,
  displayedMetricsFormatters,
} from "./creativeTrayUtils";
import { HeatMap } from "../../utils/colors";
import { AdInfoRow } from "../MetricsTable/metricsTableUtils";

interface CreativeTrayProps {
  isOpen: boolean;
  onClose: () => void;

  status?: "Live"; //TODO: add more
  launchDate?: string;
  metrics: DisplayedMetrics;
  heatMaps: Record<keyof DisplayedMetrics, HeatMap>;
  adInfo: AdInfoRow; //  company, source, ad_name, ad_id, account_id;
  kpiName?: string;
}

export const CreativeTray: React.FC<CreativeTrayProps> = ({
  isOpen,
  onClose,
  status = "Live",
  launchDate = "1/12/22",
  metrics,
  heatMaps,
  adInfo,
  kpiName,
}) => {
  const [iframeFields, setIframeFields] = useState<{
    src: string;
    width: number;
    height: number;
  }>();

  const { company, ad_name, ad_id, account_id } = adInfo;

  useEffect(() => {
    if (!iframeFields && adInfo) {
      (async () => {
        try {
          const res = await MetaLambdaFetch("/getAdPreview", {
            method: "POST",
            body: {
              accountID: account_id,
              businessManager: "Tinuiti",
              adMetaId: ad_id,
            },
          });
          const parsedRes = await awaitJSON(res);
          setIframeFields({
            src: parsedRes.src,
            width: Number(parsedRes.width),
            height: Number(parsedRes.height),
          });
        } catch (e) {}
      })();
    }
  }, [iframeFields, setIframeFields, adInfo, ad_id, account_id]);

  const [signedImageUrl, setSignedImageUrl] = useState("");

  const bucketName = "bpm-cdn";
  const s3Key = `creativeAssets/metaThumbnails/${company}/${account_id}/${ad_id}.jpg`;

  useEffect(() => {
    let isMounted = true;
    const getSignedUrl = async () => {
      if (s3Key && isMounted) {
        try {
          const signedUrl = await GetS3SignedUrl2(bucketName, s3Key);
          if (isMounted) {
            const base64Url = await convertImageToBase64(signedUrl);
            setSignedImageUrl(base64Url);
          }
        } catch (error) {
          console.error("Error getting signed URL:", error);
        }
      }
    };
    getSignedUrl();
    return () => {
      isMounted = false;
    };
  }, [s3Key]);

  const convertImageToBase64 = (imageUrl: string): Promise<string> => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      return new Promise((resolve, reject) => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/png");
          resolve(dataUrl);
        };
        img.onerror = reject;
        img.crossOrigin = "anonymous";
        img.src = imageUrl;
      });
    } catch (error) {
      console.error("Failed to convert image to base64:", error);
      return Promise.resolve(imageUrl);
    }
  };

  const downloadTray = useCallback(async () => {
    const contents = document.querySelector<HTMLElement>(".creative-tray-container");
    if (!contents) {
      return;
    }

    try {
      const canvas = await html2canvas(contents, {
        allowTaint: true,
        useCORS: true,
        scale: 2,
        onclone: (doc: Document) => {
          const wrapper = doc.querySelector(".iframe-outer") as HTMLElement | null;
          if (!wrapper) {
            return;
          }
          wrapper.style.width = "200px";
          wrapper.style.height = "200px";

          const iframe = wrapper.querySelector(".creative-iframe") as HTMLElement | null;
          if (iframe) {
            iframe.style.display = "none";
          }

          const img = doc.createElement("img");
          img.src = signedImageUrl;
          img.alt = ad_name;
          img.style.width = "200px";
          img.style.height = "200px";
          wrapper.appendChild(img);
        },
      });

      const dataUrl = canvas.toDataURL("image/png");
      downloadjs(dataUrl, `${ad_name}_CreativeTray.png`, "image/png");
    } catch (error) {
      console.error("Failed to capture image:", error);
    }
  }, [ad_name, signedImageUrl]);

  if (!isOpen) {
    return null;
  }

  const MAX_WIDTH = 200;
  const MAX_HEIGHT = 300;
  const w = iframeFields?.width ?? MAX_WIDTH;
  const h = iframeFields?.height ?? MAX_HEIGHT;
  const scale = Math.min(MAX_WIDTH / w, MAX_HEIGHT / h, 1);
  const displayWidth = Math.round(w * scale);
  const displayHeight = Math.round(h * scale);

  return (
    <div className="creative-tray-container">
      <div data-html2canvas-ignore="true" className="creative-tray-header">
        <DownloadDropdown
          size="sm"
          menuOptions={["PNG"]}
          onClickOptions={[downloadTray]}
          disabledMenuOptions={{ PNG: signedImageUrl === "" }}
        />
        <Button
          type={ButtonType.EMPTY}
          className="close-button"
          icon={<MdClose />}
          variant={ButtonFrameworkVariant.ICON_ONLY}
          onClick={onClose}
        />
      </div>
      <div className="creative-tray">
        <div className="creative-thumbnail">
          <div className="iframe-outer" style={{ width: displayWidth, height: displayHeight }}>
            <iframe
              className="creative-iframe"
              src={iframeFields?.src}
              title={ad_name}
              width={w}
              height={h}
              style={{
                width: w,
                height: h,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                border: 0,
              }}
              data-html2canvas-ignore="true"
            />
          </div>
        </div>

        <div className="info-and-metrics">
          <div className="creative-info-section">
            <h2 className="creative-name">{ad_name}</h2>
            <div className="creative-status-row">
              <span className={`status-badge status-${status.toLowerCase()}`}>{status}</span>
              <span className="launch-date">Launched: {launchDate}</span>
            </div>
          </div>

          <div className="metrics-section">
            <div className="metrics-grid">
              {DISPLAYED_METRICS_LABELS.map(({ key, label }) => {
                const value = metrics[key];

                const backgroundColor = heatMaps[key](value);
                const formattedValue = displayedMetricsFormatters(key)(value);

                return label.includes("KPI") && kpiName ? (
                  <div className="metric-row">
                    <div className="metric-label-and-kpi-name">
                      <span className="metric-label">{label}</span>
                      <span className="metric-kpi-name">{kpiName}</span>
                    </div>
                    <span className="metric-value" style={{ backgroundColor }}>
                      {formattedValue}
                    </span>
                  </div>
                ) : (
                  <div className="metric-row">
                    <span className="metric-label">{label}</span>
                    <span className="metric-value" style={{ backgroundColor }}>
                      {formattedValue}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreativeTray;
